import DashboardLayout from "@/components/DashboardLayout";
import { AnimatedCounter, AnimatedCurrency, AnimatedPercentage } from "@/components/AnimatedCounter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { DollarSign, TrendingUp, Users, Phone, Target, Clock, BarChart3, Percent, LineChart, Calendar, FileSpreadsheet, Download, Loader2 } from "lucide-react";
import { useState, useMemo, useCallback } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
  Bar,
  ComposedChart,
} from "recharts";

export default function Dashboard() {
  const [selectedPlacement, setSelectedPlacement] = useState<string>("all");
  const [downloadingReport, setDownloadingReport] = useState<string | null>(null);

  const handleDownloadReport = useCallback(async (reportType: 'liquidation' | 'remit' | 'recall') => {
    setDownloadingReport(reportType);
    try {
      const response = await fetch(`/api/reports/${reportType}`);
      if (!response.ok) throw new Error('Failed to generate report');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateStr = new Date().toISOString().slice(0, 10);
      const names: Record<string, string> = {
        liquidation: `LiqReport_${dateStr}.xlsx`,
        remit: `AgencyRemit_${dateStr}.xlsx`,
        recall: `RecallReport_${dateStr}.xlsx`,
      };
      a.download = names[reportType] || `report_${dateStr}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Report download failed:', error);
      alert('Failed to download report. Please try again.');
    } finally {
      setDownloadingReport(null);
    }
  }, []);
  
  const { data: placements } = trpc.placements.list.useQuery();
  const { data: summary, isLoading: summaryLoading } = trpc.dashboard.executiveSummary.useQuery();
  const { data: kpis, isLoading: kpisLoading } = trpc.dashboard.kpis.useQuery(
    selectedPlacement !== "all" ? { placementId: parseInt(selectedPlacement) } : undefined
  );
  const { data: placementPerf } = trpc.placements.performance.useQuery(
    selectedPlacement !== "all" ? { placementId: parseInt(selectedPlacement) } : undefined
  );
  const { data: agentMetrics } = trpc.agent.metrics.useQuery(
    selectedPlacement !== "all" ? { placementId: parseInt(selectedPlacement) } : undefined
  );
  const { data: cashFlowData } = trpc.cashFlow.projections.useQuery(
    selectedPlacement !== "all" ? { placementId: parseInt(selectedPlacement) } : undefined
  );

  // Process cash flow data for weekly chart
  const chartData = useMemo(() => {
    if (!cashFlowData) return [];
    
    const { pastPayments = [], futurePayments = [] } = cashFlowData as any;
    
    // Format past payments
    const past = pastPayments.map((p: any) => ({
      week: new Date(p.week_start).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      weekStart: p.week_start,
      collected: parseFloat(p.amount) || 0,
      projected: 0,
      paymentCount: p.payment_count,
      isPast: true,
    }));
    
    // Format future payments
    const future = futurePayments.map((p: any) => ({
      week: new Date(p.week_start).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      weekStart: p.week_start,
      collected: 0,
      projected: parseFloat(p.amount) || 0,
      paymentCount: p.payment_count,
      isPast: false,
    }));
    
    // Combine and calculate cumulative
    const combined = [...past, ...future];
    let cumulative = 0;
    return combined.map((item) => {
      cumulative += item.collected + item.projected;
      return { ...item, cumulative };
    });
  }, [cashFlowData]);

  const formatCurrency = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return "$0";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(num);
  };

  const formatPercent = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return "0%";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return `${num.toFixed(2)}%`;
  };

  const formatNumber = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return "0";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return new Intl.NumberFormat("en-US").format(num);
  };

  const toNumber = (value: number | string | null | undefined): number => {
    if (value === null || value === undefined) return 0;
    return typeof value === "string" ? parseFloat(value) : value;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium mb-2">Week of {label}</p>
          {data?.collected > 0 && (
            <p className="text-sm text-emerald-500">
              Collected: {formatCurrency(data.collected)}
            </p>
          )}
          {data?.projected > 0 && (
            <p className="text-sm text-blue-500">
              Projected: {formatCurrency(data.projected)}
            </p>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            Cumulative: {formatCurrency(data?.cumulative)}
          </p>
          {data?.paymentCount > 0 && (
            <p className="text-xs text-muted-foreground">
              {data.paymentCount} payments
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Calculate totals for display
  const totalCollected = (cashFlowData as any)?.totalCollected || 0;
  const totalFuture = (cashFlowData as any)?.totalFuture || 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Structured Settlement</h1>
            <p className="text-muted-foreground">AI-Powered Debt Collection Analytics Dashboard</p>
          </div>
          <Select value={selectedPlacement} onValueChange={setSelectedPlacement}>
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder="Select Placement" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Placements</SelectItem>
              {(Array.isArray(placements) ? placements : []).map((p: any) => (
                <SelectItem key={p.id} value={String(p.id)}>{p.placement_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Executive Summary Cards with Animated Numbers */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Face Value</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                <AnimatedCurrency value={toNumber(summary?.total_face_value)} duration={2000} />
              </div>
              <p className="text-xs text-muted-foreground">
                <AnimatedCounter value={toNumber(summary?.total_accounts)} duration={1500} /> accounts across{" "}
                <AnimatedCounter value={toNumber(summary?.total_placements)} duration={1000} /> placements
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Gross Collections</CardTitle>
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-500">
                <AnimatedCurrency value={toNumber(summary?.gross_collections)} duration={2000} />
              </div>
              <p className="text-xs text-muted-foreground">
                Net: <AnimatedCurrency value={toNumber(summary?.net_collections)} duration={1800} className="text-xs" /> (after fees)
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Liquidation Rate</CardTitle>
              <Percent className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                <AnimatedPercentage value={toNumber(summary?.liquidation_rate)} duration={2000} />
              </div>
              <p className="text-xs text-muted-foreground">
                <AnimatedCounter value={toNumber(summary?.settled_accounts)} duration={1500} /> settled,{" "}
                <AnimatedCounter value={toNumber(summary?.payment_plan_accounts)} duration={1500} /> on payment plans
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Accounts</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                <AnimatedCounter value={toNumber(summary?.active_accounts)} duration={2000} />
              </div>
              <p className="text-xs text-muted-foreground">
                of <AnimatedCounter value={toNumber(summary?.total_accounts)} duration={1500} /> total accounts
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Weekly Cash Flow Chart */}
        {chartData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Weekly Cash Flow
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="collectedGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.2}/>
                      </linearGradient>
                      <linearGradient id="projectedGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.6}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="week" 
                      tick={{ fill: '#ffffff', fontSize: 11 }}
                      axisLine={{ stroke: 'hsl(var(--border))' }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis 
                      yAxisId="left"
                      tick={{ fill: '#ffffff', fontSize: 12 }}
                      axisLine={{ stroke: 'hsl(var(--border))' }}
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    />
                    <YAxis 
                      yAxisId="right"
                      orientation="right"
                      tick={{ fill: '#ffffff', fontSize: 12 }}
                      axisLine={{ stroke: 'hsl(var(--border))' }}
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="collected"
                      name="Collected"
                      fill="url(#collectedGradient)"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="projected"
                      name="Projected"
                      fill="url(#projectedGradient)"
                      radius={[4, 4, 0, 0]}
                    />
                    <Area
                      yAxisId="right"
                      type="monotone"
                      dataKey="cumulative"
                      name="Cumulative"
                      stroke="#f59e0b"
                      fill="none"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-8 mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                  <span className="text-muted-foreground">Collected: {formatCurrency(totalCollected)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-muted-foreground">Projected (8 weeks): {formatCurrency(totalFuture)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span className="text-muted-foreground">Total: {formatCurrency(totalCollected + totalFuture)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Key Performance Metrics */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Days to Contact</CardTitle>
              <Clock className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                <AnimatedCounter value={Math.round(toNumber(kpis?.daysToContact))} duration={2000} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Average days from placement to first RPC
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Days to Settlement</CardTitle>
              <Target className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                <AnimatedCounter value={Math.round(toNumber(kpis?.daysToSettlement))} duration={2000} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Average days from RPC to settlement
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Settlement Breakdown</CardTitle>
              <BarChart3 className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-2xl font-bold text-amber-500">
                    <AnimatedPercentage value={toNumber(kpis?.settlementBreakdown?.sif50?.percent)} duration={2000} decimals={0} />
                  </div>
                  <p className="text-xs text-muted-foreground">50% SIF</p>
                </div>
                <div className="text-muted-foreground">/</div>
                <div>
                  <div className="text-2xl font-bold text-emerald-500">
                    <AnimatedPercentage value={toNumber(kpis?.settlementBreakdown?.sif60?.percent)} duration={2000} decimals={0} />
                  </div>
                  <p className="text-xs text-muted-foreground">60% SIF</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Settlement in full acceptance rates
              </p>
            </CardContent>
          </Card>
        </div>

        {/* AI Agent Performance Summary with Animated Numbers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              AI Agent Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Total Calls</p>
                <p className="text-2xl font-bold">
                  <AnimatedCounter value={toNumber(agentMetrics?.total_calls)} duration={2000} />
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">RPC Rate</p>
                <p className="text-2xl font-bold text-blue-500">
                  <AnimatedPercentage value={toNumber(agentMetrics?.rpc_rate)} duration={2000} />
                </p>
                <p className="text-xs text-muted-foreground">
                  <AnimatedCounter value={toNumber(agentMetrics?.rpc_count)} duration={1500} /> right party contacts
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">PTP Rate</p>
                <p className="text-2xl font-bold text-emerald-500">
                  <AnimatedPercentage value={toNumber(agentMetrics?.ptp_rate)} duration={2000} />
                </p>
                <p className="text-xs text-muted-foreground">
                  <AnimatedCounter value={toNumber(agentMetrics?.ptp_count)} duration={1500} /> promises to pay
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Settlements Accepted</p>
                <p className="text-2xl font-bold text-amber-500">
                  <AnimatedCounter value={toNumber(agentMetrics?.settlements_accepted)} duration={2000} />
                </p>
                <p className="text-xs text-muted-foreground">
                  <AnimatedCurrency value={toNumber(agentMetrics?.total_settlement_value)} duration={1800} className="text-xs" /> value
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Placement Performance Table */}
        {placementPerf && Array.isArray(placementPerf) && placementPerf.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Placement Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2 font-medium">Placement</th>
                      <th className="text-right py-3 px-2 font-medium">Face Value</th>
                      <th className="text-right py-3 px-2 font-medium">Accounts</th>
                      <th className="text-right py-3 px-2 font-medium">Gross Collected</th>
                      <th className="text-right py-3 px-2 font-medium">Liquidation</th>
                      <th className="text-right py-3 px-2 font-medium">ROI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {placementPerf.map((p: any) => (
                      <tr key={p.id} className="border-b">
                        <td className="py-3 px-2">
                          <div className="font-medium">{p.placement_name}</div>
                          <div className="text-xs text-muted-foreground">{p.portfolio_type}</div>
                        </td>
                        <td className="text-right py-3 px-2">{formatCurrency(p.face_value)}</td>
                        <td className="text-right py-3 px-2">{formatNumber(p.total_accounts)}</td>
                        <td className="text-right py-3 px-2 text-emerald-500">{formatCurrency(p.gross_collected)}</td>
                        <td className="text-right py-3 px-2">{formatPercent(p.liquidation_rate)}</td>
                        <td className="text-right py-3 px-2 text-blue-500">{formatPercent(p.roi)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Client Reports Export */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-emerald-500" />
              Client Reports
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Download Heights Finance formatted Excel reports for client reporting
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Liquidation Report */}
              <div className="border rounded-lg p-4 hover:border-emerald-500/50 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="h-4 w-4 text-emerald-500" />
                  <h4 className="font-semibold text-sm">Liquidation Report</h4>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Portfolio performance scorecard showing cash collected vs face value by package
                </p>
                <button
                  onClick={() => handleDownloadReport('liquidation')}
                  disabled={downloadingReport === 'liquidation'}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-800 text-white text-sm rounded-md transition-colors"
                >
                  {downloadingReport === 'liquidation' ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</>
                  ) : (
                    <><Download className="h-4 w-4" /> Download .xlsx</>
                  )}
                </button>
              </div>

              {/* Agency Remit Report */}
              <div className="border rounded-lg p-4 hover:border-blue-500/50 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="h-4 w-4 text-blue-500" />
                  <h4 className="font-semibold text-sm">Agency Remit</h4>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Per-payment detail with 25% agency / 75% client fee split for remittance
                </p>
                <button
                  onClick={() => handleDownloadReport('remit')}
                  disabled={downloadingReport === 'remit'}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white text-sm rounded-md transition-colors"
                >
                  {downloadingReport === 'remit' ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</>
                  ) : (
                    <><Download className="h-4 w-4" /> Download .xlsx</>
                  )}
                </button>
              </div>

              {/* Recall Report */}
              <div className="border rounded-lg p-4 hover:border-amber-500/50 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="h-4 w-4 text-amber-500" />
                  <h4 className="font-semibold text-sm">Recall Template</h4>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Account status reconciliation report with Close/Keep disposition codes
                </p>
                <button
                  onClick={() => handleDownloadReport('recall')}
                  disabled={downloadingReport === 'recall'}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-800 text-white text-sm rounded-md transition-colors"
                >
                  {downloadingReport === 'recall' ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</>
                  ) : (
                    <><Download className="h-4 w-4" /> Download .xlsx</>
                  )}
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
